import React, { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useTenant } from "@/contexts/TenantContext";
import api from "@/lib/api";
import {
  LayoutDashboard, Gavel, Trophy, Users, Settings, Radio,
  Tv, ClipboardList, Shirt, LogOut, Building2, UserPlus, CalendarDays,
  Megaphone, Network, ShieldCheck, PenSquare, History, FileDown, Lock, Globe2, Target, Activity,
  Flag, User2, Wallet, Layers, Ban, Sparkles,
} from "lucide-react";
import { roleHome } from "@/lib/roleHome";
import { Button } from "@/components/ui/button";
import { Logo, KenyaStripe } from "@/components/Logo";
import NotificationBell from "@/components/NotificationBell";

const NAV = [
  { to: "/dashboard",         label: "Dashboard",        icon: LayoutDashboard, key: "dashboard",         roles: ["super_admin", "league_admin"], testid: "nav-dashboard" },
  { to: "/referee",           label: "Referee Portal",   icon: Flag,            key: "referee_portal",    roles: ["court_umpire", "super_admin", "league_admin"], testid: "nav-referee" },
  { to: "/owner",             label: "Command Center",   icon: Target,          key: "owner_command",     roles: ["owner", "super_admin", "league_admin"], testid: "nav-owner-command" },
  { to: "/my-team",           label: "My Team",          icon: Shirt,           key: "my_team",           roles: ["owner", "super_admin", "league_admin"], testid: "nav-my-team" },
  { to: "/player",            label: "Player Portal",    icon: User2,           key: "player_portal",     roles: ["spectator", "super_admin", "league_admin"], testid: "nav-player" },
  { to: "/spectator",         label: "Live Broadcast",   icon: Tv,              key: "spectator",         roles: ["super_admin", "league_admin", "court_umpire", "owner", "spectator"], testid: "nav-spectator" },
  { to: "/admin/display",     label: "Public Display",   icon: Radio,           key: "public_display",    roles: ["super_admin", "league_admin"], testid: "nav-public-display" },
  { to: "/admin/season3-import", label: "Season 3 Import", icon: FileDown,     key: "s3_import",         roles: ["super_admin", "league_admin"], testid: "nav-s3-import" },
  { to: "/admin/groups",      label: "Groups",           icon: Network,         key: "s3_groups",         roles: ["super_admin", "league_admin"], testid: "nav-s3-groups" },
  { to: "/admin/team-owners", label: "Team Owners",      icon: ShieldCheck,     key: "team_owners_admin", roles: ["super_admin", "league_admin"], testid: "nav-team-owners-admin" },
  { to: "/admin/team-corrections", label: "Team Corrections", icon: Wallet,     key: "team_corrections",  roles: ["super_admin", "league_admin"], testid: "nav-team-corrections" },
  { to: "/admin/match-formats", label: "Match Formats",    icon: Layers,          key: "match_formats",     roles: ["super_admin", "league_admin"], testid: "nav-match-formats" },
  { to: "/admin/court-blocks",  label: "Court Blocks",     icon: Ban,             key: "court_blocks",      roles: ["super_admin", "league_admin"], testid: "nav-court-blocks" },
  { to: "/broadcast-studio",  label: "Broadcast Studio", icon: Radio,           key: "broadcast_studio",  roles: ["super_admin", "league_admin", "court_umpire"], testid: "nav-broadcast-studio" },
  { to: "/tv/scoreboard",     label: "TV Scoreboard",    icon: Tv,              key: "tv_scoreboard",     roles: ["super_admin", "league_admin", "court_umpire"], testid: "nav-tv-scoreboard" },
  { to: "/auction",           label: "Live Auction",     icon: Gavel,           key: "auction",           roles: ["super_admin", "league_admin", "owner", "spectator"], testid: "nav-auction" },
  { to: "/matches",           label: "Matches",          icon: Radio,           key: "matches",           roles: ["super_admin", "league_admin", "court_umpire"], testid: "nav-matches" },
  { to: "/schedule",          label: "Schedule",         icon: CalendarDays,    key: "schedule",          roles: ["super_admin", "league_admin", "court_umpire", "owner", "spectator"], testid: "nav-schedule" },
  { to: "/bracket",           label: "Bracket",          icon: Network,         key: "bracket",           roles: ["super_admin", "league_admin", "court_umpire", "owner", "spectator"], testid: "nav-bracket" },
  { to: "/admin/knockouts",   label: "KO Bracket",       icon: Trophy,          key: "ko_bracket",        roles: ["super_admin", "league_admin"], testid: "nav-ko-bracket" },
  { to: "/admin/qf-qualification", label: "QF Qualification", icon: Sparkles,   key: "qf_qual",           roles: ["super_admin", "league_admin"], testid: "nav-qf-qualification" },
  { to: "/admin/seeding",     label: "Edit Seeding & Scores", icon: Sparkles,   key: "seeding_admin",     roles: ["super_admin", "league_admin"], testid: "nav-seeding-admin" },
  { to: "/admin/widget",      label: "Live Widget",      icon: Radio,           key: "embed_widget",      roles: ["super_admin", "league_admin"], testid: "nav-embed-widget" },
  { to: "/players",           label: "Players",          icon: Users,           key: "players",           roles: ["super_admin", "league_admin", "owner", "spectator"], testid: "nav-players" },
  { to: "/registrations",     label: "Registrations",    icon: UserPlus,        key: "registrations",     roles: ["super_admin", "league_admin"], testid: "nav-registrations" },
  { to: "/teams",             label: "Teams",            icon: Trophy,          key: "teams",             roles: ["super_admin", "league_admin", "owner", "spectator"], testid: "nav-teams" },
  { to: "/operations",        label: "Operations",       icon: ClipboardList,   key: "operations",        roles: ["super_admin", "league_admin"], testid: "nav-operations" },
  { to: "/meetings",          label: "Owners Meetings",  icon: Megaphone,       key: "meetings",          roles: ["super_admin", "league_admin", "owner"], testid: "nav-meetings" },
  { to: "/jerseys",           label: "Branding",         icon: Shirt,           key: "jerseys",           roles: ["super_admin", "league_admin", "owner"], testid: "nav-jerseys" },
  { to: "/prize-pool",        label: "Prize Pool",       icon: Building2,       key: "prize_pool",        roles: ["super_admin", "league_admin", "owner", "spectator"], testid: "nav-prize-pool" },
  { to: "/sponsor-inquiries", label: "Sponsor Leads",    icon: Megaphone,       key: "sponsor_inquiries", roles: ["super_admin", "league_admin"], testid: "nav-sponsor-inquiries" },
  { to: "/tenants",           label: "Tenants",          icon: Globe2,          key: "tenants",           roles: ["super_admin"], testid: "nav-tenants" },
  { to: "/users",             label: "User Management",  icon: ShieldCheck,     key: "users",             roles: ["super_admin"], testid: "nav-users" },
  { to: "/cms",               label: "Site Content",     icon: PenSquare,       key: "cms",               roles: ["super_admin", "league_admin"], testid: "nav-cms" },
  { to: "/audit-logs",        label: "Audit Logs",       icon: History,         key: "audit_logs",        roles: ["super_admin"], testid: "nav-audit" },
  { to: "/events",            label: "Event Log",        icon: Activity,        key: "event_log",         roles: ["super_admin", "league_admin"], testid: "nav-events" },
  { to: "/exports",           label: "Exports",          icon: FileDown,        key: "exports",           roles: ["super_admin", "league_admin"], testid: "nav-exports" },
  { to: "/role-permissions",  label: "Role Permissions", icon: Lock,            key: "role_permissions",  roles: ["super_admin"], testid: "nav-role-perms" },
  { to: "/admin-config",      label: "Master Config",    icon: Settings,        key: "admin_config",      roles: ["super_admin", "league_admin"], testid: "nav-config" },
];

function TenantBadge() {
  const { tenant, activeSlug, switchTenant } = useTenant();
  const { user } = useAuth();
  const [tenants, setTenants] = useState([]);
  const [open, setOpen] = useState(false);
  const isSuper = user?.role === "super_admin";

  useEffect(() => {
    if (!isSuper || !open) return;
    (async () => {
      try {
        const { data } = await api.get("/tenants");
        setTenants(data);
      } catch { /* ignore */ }
    })();
  }, [isSuper, open]);

  if (!tenant) return null;
  const color = tenant.theme_config?.brand_primary || "#DC2626";
  return (
    <div className="relative">
      <button
        data-testid="tenant-badge"
        onClick={() => isSuper && setOpen((v) => !v)}
        className={`w-full flex items-center gap-2 px-3 py-2 rounded-md border border-[#16322a] bg-[#0d1c15] text-left ${isSuper ? "hover:border-emerald-700 cursor-pointer" : "cursor-default"}`}
      >
        <div className="w-6 h-6 rounded flex items-center justify-center text-[10px] font-black text-white shrink-0"
             style={{ background: color }}>
          {tenant.name?.slice(0, 1).toUpperCase()}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500">Tenant</div>
          <div className="text-xs text-white truncate font-medium">{tenant.name}</div>
        </div>
        {isSuper && <Globe2 className="w-3.5 h-3.5 text-zinc-500 shrink-0" />}
      </button>

      {isSuper && open && (
        <div className="absolute left-0 right-0 top-full mt-1 z-30 rounded-md border border-[#16322a] bg-[#0d1c15] shadow-2xl max-h-72 overflow-y-auto">
          {tenants.map((t) => {
            const active = t.slug === (activeSlug || tenant.slug);
            return (
              <button
                key={t.id}
                data-testid={`tenant-switch-${t.slug}`}
                onClick={async () => { setOpen(false); await switchTenant(t.slug); }}
                className={`w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-[#11241b] ${active ? "bg-[#11241b]" : ""}`}
              >
                <div className="w-5 h-5 rounded text-[9px] font-black text-white flex items-center justify-center shrink-0"
                     style={{ background: t.theme_config?.brand_primary || "#DC2626" }}>
                  {t.name?.slice(0, 1).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-xs text-white truncate">{t.name}</div>
                  <div className="text-[10px] text-zinc-500 font-mono truncate">/{t.slug}</div>
                </div>
                {active && <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 shrink-0" />}
              </button>
            );
          })}
          {tenants.length === 0 && <div className="p-3 text-xs text-zinc-500">No tenants…</div>}
        </div>
      )}
    </div>
  );
}

export default function Layout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [perms, setPerms] = useState(null);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const { data } = await api.get("/role-permissions");
        if (active) setPerms(data.permissions);
      } catch { /* silent — fall back to hard-coded roles array */ }
    })();
    return () => { active = false; };
  }, []);

  const items = NAV.filter((n) => {
    if (!n.roles.includes(user.role)) return false;
    if (user.role === "super_admin") return true; // always full
    if (perms && perms[user.role] && Object.prototype.hasOwnProperty.call(perms[user.role], n.key)) {
      return perms[user.role][n.key] === true;
    }
    return true; // default-allow until perms load
  });

  return (
    <div className="min-h-screen flex bg-[#060d09]">
      {/* Sidebar */}
      <aside className="hidden md:flex w-64 shrink-0 flex-col border-r border-[#16322a] bg-[#0a1611] sticky top-0 h-screen">
        <div className="p-5 border-b border-[#16322a]">
          <Logo className="h-14 w-auto" />
          <KenyaStripe className="mt-3 w-32" />
          <div className="mt-2 text-[10px] uppercase tracking-[0.25em] text-zinc-500">Padel League · Season 03</div>
        </div>
        <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
          {items.map((it) => (
            <NavLink
              key={it.to}
              to={it.to}
              end={it.to === "/dashboard"}
              data-testid={it.testid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-all ${
                  isActive
                    ? "bg-[#00A86B] text-white font-semibold"
                    : "text-zinc-400 hover:bg-[#11241b] hover:text-white"
                }`
              }
            >
              <it.icon className="w-4 h-4" />
              <span>{it.label}</span>
            </NavLink>
          ))}
        </nav>
        <div className="p-4 border-t border-[#16322a] space-y-3">
          <TenantBadge />
          <div>
            <div className="text-xs text-zinc-500 mb-1">Signed in as</div>
            <div className="text-sm text-white truncate" data-testid="layout-user-name">{user.name}</div>
            <div className="text-[10px] uppercase tracking-widest text-[#00A86B]" data-testid="layout-user-role">{user.role}</div>
          </div>
          <Button
            data-testid="btn-logout"
            variant="outline"
            size="sm"
            className="w-full border-[#16322a] bg-[#11241b] hover:bg-[#16322a] text-white"
            onClick={async () => { await logout(); nav("/admin"); }}
          >
            <LogOut className="w-3.5 h-3.5 mr-1.5" /> Logout
          </Button>
        </div>
      </aside>

      {/* Mobile top bar */}
      <div className="md:hidden fixed top-0 inset-x-0 z-40 bg-[#0a1611]/95 backdrop-blur border-b border-[#16322a] px-4 py-3 flex items-center justify-between">
        <Logo className="h-8 w-auto" />
        <Button size="sm" variant="outline" className="border-[#16322a] bg-[#11241b]" onClick={async () => { await logout(); nav("/admin"); }}>
          <LogOut className="w-4 h-4" />
        </Button>
      </div>
      <div className="md:hidden fixed bottom-0 inset-x-0 z-40 bg-[#0a1611]/95 backdrop-blur border-t border-[#16322a] overflow-x-auto">
        <div className="flex gap-1 px-2 py-2">
          {items.slice(0, 6).map((it) => (
            <NavLink
              key={it.to}
              to={it.to}
              end={it.to === "/dashboard"}
              data-testid={`m-${it.testid}`}
              className={({ isActive }) =>
                `flex flex-col items-center gap-0.5 px-3 py-1.5 rounded-md text-[10px] shrink-0 ${
                  isActive ? "bg-[#00A86B] text-white" : "text-zinc-400"
                }`
              }
            >
              <it.icon className="w-4 h-4" />
              <span>{it.label}</span>
            </NavLink>
          ))}
        </div>
      </div>

      <main className="flex-1 min-w-0 pt-16 md:pt-0 pb-24 md:pb-0 relative">
        {/* Top-right brand mark + Kenya stripe + notification bell — desktop only */}
        <div className="hidden md:flex items-center gap-3 absolute top-4 right-6 z-10" data-testid="topbar-logo">
          <div data-testid="notification-bell-desktop"><NotificationBell /></div>
          <div className="kenya-stripe w-24 rounded-full pointer-events-none" />
          <Logo variant="inline" className="h-9 pointer-events-none" />
        </div>
        {/* Mobile bell — top-right */}
        <div className="md:hidden fixed top-3 right-3 z-40" data-testid="notification-bell-mobile">
          <NotificationBell />
        </div>
        <Outlet />
      </main>
    </div>
  );
}
